import { useCallback, useState } from 'react'
import { pdf } from '@react-pdf/renderer'
import { CleanTemplate } from '../components/cv-templates/CleanTemplate'
import { BoldTemplate } from '../components/cv-templates/BoldTemplate'
import { CreativeTemplate } from '../components/cv-templates/CreativeTemplate'
import { TechTemplate } from '../components/cv-templates/TechTemplate'
import { ExecutiveTemplate } from '../components/cv-templates/ExecutiveTemplate'
import { ModernTemplate } from '../components/cv-templates/ModernTemplate'
import type { CV } from '../types/cv'
import type { TemplateType } from '../utils/colorPalettes'

export type { TemplateType }

interface UsePdfGeneratorReturn {
  isGenerating: boolean
  error: string | null
  generatePdf: (cv: CV, template?: TemplateType, colorPalette?: string) => Promise<Blob>
  downloadPdf: (cv: CV, template?: TemplateType, colorPalette?: string, filename?: string) => Promise<void>
}

export function usePdfGenerator(): UsePdfGeneratorReturn {
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generatePdf = useCallback(
    async (cv: CV, template: TemplateType = 'clean', colorPalette?: string): Promise<Blob> => {
      setIsGenerating(true)
      setError(null)
      try {
        let doc
        switch (template) {
          case 'bold':
            doc = BoldTemplate({ cv, colorPalette })
            break
          case 'creative':
            doc = CreativeTemplate({ cv, colorPalette })
            break
          case 'tech':
            doc = TechTemplate({ cv, colorPalette })
            break
          case 'executive':
            doc = ExecutiveTemplate({ cv, colorPalette })
            break
          case 'modern':
            doc = ModernTemplate({ cv, colorPalette })
            break
          case 'clean':
          default:
            doc = CleanTemplate({ cv, colorPalette })
        }
        const blob = await pdf(doc).toBlob()
        return blob
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Error generating PDF'
        setError(message)
        throw err
      } finally {
        setIsGenerating(false)
      }
    },
    []
  )

  const downloadPdf = useCallback(
    async (
      cv: CV,
      template: TemplateType = 'clean',
      colorPalette?: string,
      filename?: string
    ): Promise<void> => {
      const blob = await generatePdf(cv, template, colorPalette)
      const name =
        filename ||
        `cv-${cv.personalInfo?.fullName?.toLowerCase().replace(/\s+/g, '-') || 'documento'}.pdf`

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    [generatePdf]
  )

  return {
    isGenerating,
    error,
    generatePdf,
    downloadPdf,
  }
}
